import {Ways} from "../components/Ways";
import {TheCrypt} from "../TheCrypt";
import {TimelineLite} from "gsap";
import Spine = PIXI.spine.Spine;
import {TheCryptHelper} from "../utils/TheCryptHelper";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {TheCryptWinConfig} from "../configs/TheCryptWinConfig";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";

export class TheCryptReelFrame extends PIXI.Container {
    private _ways: Ways;
    private _reelFrame: Spine;
    private _eye: Spine;
    private _isIdling: boolean = false;
    private _idleAnimationPlaying: boolean = false;

    private _startDelay: number = 15;
    private _intervalRange: [number, number] = [10, 30];
    private _idleTl: TimelineLite | null;

    constructor() {
        super();
        this._isIdling = false;
        this.name = "ReelFrame";
        this.init();
    }

    private init() {
        this.position.set(360, 360);
        const reelFrame: Spine = new Spine(TheCrypt.assetLoader.getSpineAsset("reelFrame"));
        reelFrame.name = "reelFrame";
        reelFrame.position.set(-4, 20);
        this.addChild(reelFrame);
        this._reelFrame = reelFrame;
        this._reelFrame.state.setAnimation(0, `base`, true);

        const eye: Spine = new Spine(TheCrypt.assetLoader.getSpineAsset("eyeAnimation"));
        eye.name = "eye";
        eye.position.set(0, -313);
        this.addChild(eye);
        this._eye = eye;
        this._eye.state.setAnimation(0, `base`, true);

        this._ways = new Ways();
        this._ways.position.set(-70, -315);
        this.addChild(this._ways);
    }

    public reset(): void {
        this._ways.reset();
    }

    public updateWays(totalWays: number): TimelineLite {
        return this._ways.updateValue(totalWays);
    }

    public setMode(mode: string): void {
        this._reelFrame.skeleton.setSkinByName(TheCryptHelper.isNormal(mode) ? "main" : "FS");
    }

    public onThirdScatterLand(): void {
        new SpineTween(this._reelFrame, 1, "scatter_trigger");
        new SpineTween(this._eye, 1, `scatter_trigger`);
    }

    public onWildExpand(): TimelineLite {
        return new TimelineLite().add(new SpineTween(this._eye, 1, `wild_expand`))
            .add(() => this.resetEyeAnimation());
    }

    public onAnticipationFail(): void {
        new TimelineLite().add(new SpineTween(this._eye, 1, `no_win_${Math.ceil(Math.random() * 3)}`))
            .add(() => this.resetEyeAnimation());
    }

    public onWin(): void {
        new TimelineLite().add(new SpineTween(this._eye, 1, `win`))
            .add(() => this.resetEyeAnimation());
    }

    public playIdleAnimation(): void {
        if (!this._isIdling) {
            return;
        }
        new SpineTween(this._eye, 0, `sleep_start`);
        this._idleAnimationPlaying = true;
        this._idleTl = new TimelineLite();
        this._idleTl!.add(() => this.playIdleAnimation(), MathHelper.randomNumberInRange(this._intervalRange[0], this._intervalRange[1], 2));
    }

    public startIdle(): void {
        if (this._isIdling) {
            return;
        }

        this._isIdling = true;
        this._idleTl = new TimelineLite();
        this._idleTl.add(() => {
            this.playIdleAnimation();
        }, this._startDelay);
    }

    public onSymbolsDropOut(): void {
        new SpineTween(this._reelFrame, 1, `spin`);
    }

    public stopIdle(): void {
        this._isIdling = false;
        if (this._idleTl) {
            this._idleTl.pause();
            this._idleTl.kill();
        }

        this._idleTl = null;
        if (this._idleAnimationPlaying) {
            this._idleAnimationPlaying = false;
            new TimelineLite().add([
                new SpineTween(this._eye, 0, `wake_up`)
            ]).add(() => {
                const trackEntry = this._eye.state.setAnimation(0, `base`, true);
                trackEntry.mixDuration = 0.2;
            });
        }

    }

    public onWinToggle(): void {
        const winRatio: number = TheCrypt.model.avalancheWinRatio,
            bigWinRatio = TheCryptWinConfig.WIN_RATIOS.bigWin;

        if (winRatio < bigWinRatio[0].ratio) {
            return;
        }
        TheCryptSoundManager.playSound("bigwinInit");
        if (winRatio >= bigWinRatio[2].ratio) {
            new SpineTween(this._reelFrame, 1, `initial_bigwin_3`);
        } else if (winRatio >= bigWinRatio[1].ratio) {
            new SpineTween(this._reelFrame, 1, `initial_bigwin_2`);
        } else {
            new SpineTween(this._reelFrame, 1, `initial_bigwin_1`);
        }
    }

    public updateEyeAnimation(reelId: number): void {
        const trackEntry = this._eye.state.setAnimation(1, `anticipation_${reelId + 1}`, true);
        trackEntry.mixDuration = 0.2;
    }

    public resetEyeAnimation(): void {
        const trackEntry = this._eye.state.setEmptyAnimation(1, 0);
        trackEntry.mixDuration = 0.2;
    }
}